import React, { ReactNode, useState } from "react";
import { LinhaTabela } from "shared/components/table/LinhaTabela";
import { RegistroTabela } from "shared/components/table/RegistroTabela";
import {
  formatarValorMonetario,
  formatarDataString,
  formatarDataStringParaMesAno,
} from "shared/util/functions/formatacoesDeValores/FormatarValores";
import { CheckBox } from "shared/components/checkbox/CheckBox";
import { Tooltip } from "antd";
import { FaEye } from "react-icons/fa";
import ModalVisualizarContaReceber from "./ModalVisualizarContaReceber";
import { OpcoesSelecionaveisCheckBox } from "types/OpcoesSelecionaveisCheckBoxType";
import { RegistroContaReceberDTO } from "types/dto/RegistroContaReceberDTO";
import { BsFillTrashFill } from "react-icons/bs";
import { useDeleteCobrancaProcessoJudicial } from "hooks/processosJudiciais/useDeleteCobrancaProcessoJudicial";
import showAlert from "shared/util/functions/alert/showAlert";
import { mensagemPadraoExclusao } from "shared/util/functions/alert/MensagensPadrõesAlerta";
import { toast } from "react-hot-toast";
import { possuiMensagemDeErro } from "shared/util/functions/errors/RequestPossuiMensagemDeErro";
import { getMensagemDeErroBackend } from "shared/util/functions/errors/GetMensagemDeErroBackend";
import { useProcessosJudiciaisContext } from "context/gestaoCobranca/ProcessosJudiciaisContext";

interface RenderRowsProps {
  opcaoSelecionadaCheckBox: OpcoesSelecionaveisCheckBox;
  idsCobrancasSelecionadas: number[];
  onSelecionarCobranca: (cobranca: RegistroContaReceberDTO) => void;
  onDesmarcarCobranca: (cobranca: RegistroContaReceberDTO) => void;
  registros: RegistroContaReceberDTO[];
  isVisualizacao: boolean;
}

export const RenderRowsCobrancasVinculadasAoProcesso: React.FC<RenderRowsProps> = (props) => {
  const [showModalVisualizarCobranca, setShowModalVisualizarCobranca] = useState(false);
  const [contaReceberId, setContaReceberId] = useState<number>();
  const { mutateAsync } = useDeleteCobrancaProcessoJudicial();
  const { possuiPermissaoExcluirProcessosJudiciais, possuiPermissaoIniciarAcordo } = useProcessosJudiciaisContext();

  const excluirRegistro = (cobrancaId: number) => {
    showAlert({
      texto: mensagemPadraoExclusao(),
      tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
      onConfirm: async () => {
        try {
          await mutateAsync(cobrancaId);
        } catch (error) {
          if (possuiMensagemDeErro(error)) {
            toast.error(getMensagemDeErroBackend(error));
          } else {
            toast.error("Erro ao excluir cobrança");
          }
        }
      },
    });
  };

  const renderRegistros = (): ReactNode => {
    return props.registros.map((cobranca, index) => {
      const isChecked =
        props.opcaoSelecionadaCheckBox === "TODAS_FILTRO" || props.idsCobrancasSelecionadas.includes(cobranca.id);

      return (
        <LinhaTabela
          isSelecionada={isChecked}
          id={`id-registro-cobrancas-${index}`}
          key={`key-registro-cobrancas-${index}`}
        >
          {possuiPermissaoIniciarAcordo && (
            <RegistroTabela
              isCheckBox
              id={`check-box-cobrancas-${cobranca.id}`}
              key={`check-box-cobrancas-${cobranca.id}`}
            >
              <CheckBox
                id={`check-box-${cobranca.id}`}
                usarValidacaoParaFormulario={false}
                key={`check-box-${cobranca.id}`}
                disabled={props.isVisualizacao || props.opcaoSelecionadaCheckBox === "TODAS_FILTRO"}
                checked={
                  props.opcaoSelecionadaCheckBox === "TODAS_FILTRO" ||
                  props.idsCobrancasSelecionadas.includes(cobranca.id)
                }
                onChange={(checked: boolean) => {
                  checked ? props.onSelecionarCobranca(cobranca) : props.onDesmarcarCobranca(cobranca);
                }}
              />
            </RegistroTabela>
          )}

          <RegistroTabela id={`mes-competencia-${cobranca.id}`} key={`mes-competencia-${cobranca.id}`}>
            <span>{formatarDataStringParaMesAno(cobranca.mesCompetencia)}</span>
          </RegistroTabela>

          <RegistroTabela id={`nosso-numero-cobranca-${cobranca.id}`} key={`nosso-numero-cobranca-${cobranca.id}`}>
            <span>{cobranca.nossoNumero ? cobranca.nossoNumero : "-"}</span>
          </RegistroTabela>

          <RegistroTabela id={`vencimento-cobranca-${cobranca.id}`} key={`vencimento-cobranca-${cobranca.id}`}>
            <span>{formatarDataString(cobranca.dataVencimento)}</span>
          </RegistroTabela>

          <RegistroTabela id={`valor-cobranca-${cobranca.id}`} key={`valor-cobranca-${cobranca.id}`}>
            <span>{formatarValorMonetario(cobranca.valor)}</span>
          </RegistroTabela>

          <RegistroTabela
            id={`acoes-selecionar-cobranca-${cobranca.id}`}
            key={`acoes-selecionar-cobranca-${cobranca.id}`}
          >
            <Tooltip title="Visualizar cobrança" placement="top">
              <a>
                <FaEye
                  className="cursor-pointer me-3"
                  onClick={() => {
                    setContaReceberId(cobranca.id);
                    setShowModalVisualizarCobranca(true);
                  }}
                  size={15}
                  color="#a69ae7"
                />
              </a>
            </Tooltip>
            {possuiPermissaoExcluirProcessosJudiciais && !props.isVisualizacao && (
              <Tooltip title="Excluir do processo" placement="top">
                <a>
                  <BsFillTrashFill
                    className="cursor-pointer me-2"
                    onClick={() => excluirRegistro(cobranca.id)}
                    size={15}
                    color="#a69ae7"
                  />
                </a>
              </Tooltip>
            )}
          </RegistroTabela>
        </LinhaTabela>
      );
    });
  };

  return (
    <>
      {props.registros.length > 0 && renderRegistros()}
      {contaReceberId && (
        <ModalVisualizarContaReceber
          closeModal={() => setShowModalVisualizarCobranca(false)}
          showModal={showModalVisualizarCobranca}
          contaReceberId={contaReceberId}
        />
      )}
    </>
  );
};
